import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useIndexingOperationsStore } from "@/stores/indexing-operations.store";
import {
  documentHtmlQueryKey,
  invalidateDocumentHtml,
} from "@/lib/documentHtmlQuery";
import { Pagination } from "../models/base/Pagination";
import Workspace, {
  WorkspaceLinkRequest,
  WorkspaceRole,
} from "../models/workspace/Workspace";
import { PATH_KEYS } from "../services/appServices";
import { useItemsCrud } from "./useItemsCrud";
import { useItemService } from "./useServices";
import {
  DocumentRequestBodyWithMetadataAndDocumentId,
  SyncDocumentRequestBody,
} from "@/services/workspaceServices";
import { WorkspaceDocumentMetadata } from "@/models/metadata/metadata";
import { FolderMetadataRecord } from "@/types/folder-metadata";
import type { OrphanedWorkspaceSortBy } from "@/services/workspaceServices";
import { RestError, restError } from "@/models/client/RestResponse";

const useWorkspace = () => {
  /**** Services ****/
  const queryClient = useQueryClient();
  const workspaceService = useItemService((services) => services.workspace);

  const workspaceCrud = useItemsCrud<Workspace>(
    [PATH_KEYS.workspace],
    (services) => services.workspace
  );

  const invalidateWorkspaceQueries = () => {
    queryClient.invalidateQueries({
      queryKey: [PATH_KEYS.workspace],
      exact: false,
    });
  };

  const deleteWorkspace = async (id: string) => {
    const response = await workspaceService.delete(id);
    if (response.success) return response.value;
    throw restError(response);
  };

  const claimWorkspaceOwnership = async (
    workspaceId: string,
    newOwnerId: string
  ) => {
    const response = await workspaceService.claimWorkspace(
      workspaceId,
      newOwnerId
    );
    if (response.success) return response.value;
    throw restError(response);
  };

  // Overrides workspaceCrud.remove: broad invalidation refreshes orphaned list and related queries.
  const remove = useMutation({
    mutationKey: [PATH_KEYS.workspace, "remove"],
    mutationFn: deleteWorkspace,
    onSuccess: invalidateWorkspaceQueries,
  });

  /**** Queries & Mutations ****/
  const useGetDocumentHtml = (
    workspaceId?: string,
    documentId?: string,
    blobId?: string,
    revisionVersion?: number
  ) =>
    useQuery<string | null>({
      // Own key root: see documentHtmlQuery for why broad workspace
      // invalidations must not refetch an open document.
      queryKey: documentHtmlQueryKey(
        workspaceId,
        documentId,
        blobId,
        revisionVersion
      ),
      queryFn: async () => {
        if (!workspaceId) throw new Error("Workspace ID is required");
        if (!documentId) throw new Error("Document ID is required");
        if (!blobId) throw new Error("Blob ID is required");
        if (revisionVersion === undefined)
          throw new Error("Revision version is required");
        const response = await workspaceService.getDocumentHtml(
          workspaceId,
          documentId,
          blobId,
          revisionVersion
        );
        if (response.success) {
          return response.value;
        } else {
          // 404 means no HTML available - this is a valid case, not an error
          // Return null instead of throwing to avoid error state and retries
          if (response.status === 404) {
            return null;
          }
          // For other errors, throw to trigger error state
          // Include status in error for retry logic
          throw restError(response);
        }
      },
      enabled:
        !!workspaceId &&
        !!documentId &&
        !!blobId &&
        revisionVersion !== undefined,
      retry: (failureCount, error) => {
        // Don't retry on 404 errors (but 404 is handled above, so this is for other cases)
        // Also don't retry on 4xx errors (client errors) - only retry on server errors (5xx)
        if (
          error instanceof RestError &&
          error.status >= 400 &&
          error.status < 500
        ) {
          return false;
        }
        // Retry up to 1 time for server errors (5xx)
        return failureCount < 1;
      },
    });

  const useQueryAllWorkspaces = () =>
    useQuery<Workspace[]>({
      queryKey: [PATH_KEYS.workspace],
      queryFn: async () => {
        const response = await workspaceService.getAllWorkspaces();
        if (response.success) return response.value;
        else throw restError(response);
      },
    });

  const addDocuments = useMutation({
    mutationKey: [PATH_KEYS.workspace, "addWorkspaceDocuments"],
    mutationFn: async ({
      workspaceId,
      requestBody,
    }: {
      workspaceId: string;
      requestBody: {
        documents: DocumentRequestBodyWithMetadataAndDocumentId[];
        sessionId: string;
      };
    }) => {
      const response = await workspaceService.addDocuments(
        workspaceId,
        requestBody
      );
      if (response.success) return response.value;
      else throw restError(response);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [PATH_KEYS.workspace],
        exact: false,
      });
      
      // Invalidate search queries so new documents appear in global search
      queryClient.invalidateQueries({
        queryKey: [PATH_KEYS.search, "tenant"],
        exact: false,
      });
    },
  });

  const moveDocuments = useMutation({
    mutationKey: [PATH_KEYS.workspace, "moveDocuments"],
    mutationFn: async ({
      workspaceId,
      requestBody,
    }: {
      workspaceId: string;
      requestBody: {
        newFolderId?: string;
        documentIds?: string[];
      };
    }) => {
      const response = await workspaceService.moveDocuments(
        workspaceId,
        requestBody
      );
      if (response.success) return response.value;
      else throw restError(response);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [PATH_KEYS.workspace],
        exact: false,
      });
    },
  });

  const syncDocument = useMutation({
    mutationKey: [PATH_KEYS.workspace, "syncWorkspaceDocument"],
    mutationFn: async ({
      workspaceId,
      documentId,
      requestBody,
    }: {
      workspaceId: string;
      documentId: string;
      requestBody: SyncDocumentRequestBody;
    }) => {
      const response = await workspaceService.syncDocument(
        workspaceId,
        documentId,
        requestBody
      );
      if (response.success) return response.value;
      else throw restError(response);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [PATH_KEYS.workspace],
        exact: false,
      });
      
      // Invalidate search queries so synced documents appear in global search
      queryClient.invalidateQueries({
        queryKey: [PATH_KEYS.search, "tenant"],
        exact: false,
      });
    },
  });

  const removeDocument = useMutation({
    mutationKey: [PATH_KEYS.workspace, "deleteWorkspaceDocument"],
    mutationFn: async ({
      workspaceId,
      documentId,
    }: {
      workspaceId: string;
      documentId: string;
    }) => {
      const response = await workspaceService.removeDocument(
        workspaceId,
        documentId
      );
      if (response.success) return response.value;
      else throw restError(response);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [PATH_KEYS.workspace],
        exact: false,
      });
      
      // Invalidate search queries so removed documents disappear from global search
      queryClient.invalidateQueries({
        queryKey: [PATH_KEYS.search, "tenant"],
        exact: false,
      });
    },
  });

  const reindexDocument = useMutation({
    mutationKey: [PATH_KEYS.workspace, "reindexDocument"],
    mutationFn: async ({
      workspaceId,
      documentId,
    }: {
      workspaceId: string;
      documentId: string;
    }) => {
      const response = await workspaceService.reindexDocument(
        workspaceId,
        documentId
      );
      if (response.success) return response.value;
      else throw restError(response);
    },
    onSuccess: (data, variables) => {
      // Async indexing (LAR-252): a background run was accepted — record it so
      // the files views show the in-progress state and the poller takes over.
      if (data.status === "indexing" && data.operationId) {
        useIndexingOperationsStore
          .getState()
          .start(variables.documentId, data.operationId);
        return;
      }
      // The worker did not confirm the start in time (LAR-620): the run may
      // still begin. Nothing completed, so ask again which runs are live.
      if (data.status === "indexing") {
        queryClient.invalidateQueries({
          queryKey: [
            PATH_KEYS.workspace,
            "runningIndexOperations",
            variables.workspaceId,
          ],
        });
        return;
      }
      useIndexingOperationsStore.getState().clear(variables.documentId);

      queryClient.invalidateQueries({
        queryKey: [PATH_KEYS.workspace],
        exact: false,
      });
      // A synchronous reindex (worker without async indexing) regenerated the HTML.
      invalidateDocumentHtml(
        queryClient,
        variables.workspaceId,
        variables.documentId
      );

      // Invalidate search queries so reindexed documents update in global search
      queryClient.invalidateQueries({
        queryKey: [PATH_KEYS.search, "tenant"],
        exact: false,
      });
    },
  });

  const removeDocuments = useMutation({
    mutationKey: [PATH_KEYS.workspace, "removeDocuments"],
    mutationFn: async ({
      workspaceId,
      requestBody,
    }: {
      workspaceId: string;
      requestBody: {
        documentIds: string[];
      };
    }) => {
      const response = await workspaceService.removeDocuments(
        workspaceId,
        requestBody
      );
      if (response.success) return response.value;
      else throw restError(response);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [PATH_KEYS.workspace],
        exact: false,
      });
      
      // Invalidate search queries so removed documents disappear from global search
      queryClient.invalidateQueries({
        queryKey: [PATH_KEYS.search, "tenant"],
        exact: false,
      });
    },
  });

  const grantAccess = useMutation({
    mutationKey: [PATH_KEYS.workspace, "GrantAccess"],
    mutationFn: async ({
      workspaceId,
      userIds,
      role,
    }: {
      workspaceId: string;
      userIds: string[];
      role: WorkspaceRole;
    }) => {
      const response = await workspaceService.grantAccess(
        workspaceId,
        userIds,
        role
      );
      if (response.success) return response.value;
      else throw restError(response);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [PATH_KEYS.workspace],
        exact: false,
      });
    },
  });

  const revokeAccess = useMutation({
    mutationKey: [PATH_KEYS.workspace, "RevokeAccess"],
    mutationFn: async ({
      workspaceId,
      userId,
    }: {
      workspaceId: string;
      userId: string;
    }) => {
      const response = await workspaceService.revokeAccess(workspaceId, userId);
      if (response.success) return response.value;
      else throw restError(response);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [PATH_KEYS.workspace],
        exact: false,
      });
    },
  });

  const updateDocumentMetadata = useMutation({
    mutationKey: [PATH_KEYS.workspace, "updateWorkspaceDocumentMetadata"],
    mutationFn: async ({
      workspaceId,
      documentId,
      requestBody,
    }: {
      workspaceId: string;
      documentId: string;
      requestBody: {
        documentSource: string;
        driveId?: string;
        siteId?: string;
        sharepointFileId?: string;
        documentMetadataList?: WorkspaceDocumentMetadata[];
      };
    }) => {
      const response = await workspaceService.updateDocumentMetadata(
        workspaceId,
        documentId,
        requestBody
      );
      if (response.success) return response.value;
      else throw restError(response);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [PATH_KEYS.workspace],
        exact: false,
      });
      
      // Invalidate search queries so updated metadata appears in global search
      queryClient.invalidateQueries({
        queryKey: [PATH_KEYS.search, "tenant"],
        exact: false,
      });
    },
  });

  const addFolder = useMutation({
    mutationKey: [PATH_KEYS.workspace, "addFolder"],
    mutationFn: async ({
      workspaceId,
      requestBody,
    }: {
      workspaceId: string;
      requestBody: {
        name?: string;
        parentFolderId?: string;
        paths?: string[];
        pathMetadataMap?: FolderMetadataRecord;
      };
    }) => {
      const response = await workspaceService.addFolder(
        workspaceId,
        requestBody
      );
      if (response.success) return response.value;
      else throw restError(response);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [PATH_KEYS.workspace],
        exact: false,
      });
    },
  });

  const renameDocument = useMutation({
    mutationKey: [PATH_KEYS.workspace, "renameDocument"],
    mutationFn: async ({
      workspaceId,
      documentId,
      requestBody,
    }: {
      workspaceId: string;
      documentId: string;
      requestBody: {
        documentName: string;
      };
    }) => {
      const response = await workspaceService.renameDocument(
        workspaceId,
        documentId,
        requestBody
      );
      if (response.success) return response.value;
      else throw restError(response);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [PATH_KEYS.workspace],
        exact: false,
      });

      // The rename writes html_table.document_title, which is the name global
      // search renders, so a cached hit would keep showing the old one.
      queryClient.invalidateQueries({
        queryKey: [PATH_KEYS.search, "tenant"],
        exact: false,
      });
    },
  });

  const updateFolder = useMutation({
    mutationKey: [PATH_KEYS.workspace, "updateFolder"],
    mutationFn: async ({
      workspaceId,
      requestBody,
    }: {
      workspaceId: string;
      requestBody: {
        folderId: string;
        folderName: string;
      };
    }) => {
      const response = await workspaceService.updateFolder(
        workspaceId,
        requestBody
      );
      if (response.success) return response.value;
      else throw restError(response);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [PATH_KEYS.workspace],
        exact: false,
      });
    },
  });

  const updateFolderMetadata = useMutation({
    mutationKey: [PATH_KEYS.workspace, "updateFolderMetadata"],
    mutationFn: async ({
      workspaceId,
      folderId,
      requestBody,
    }: {
      workspaceId: string;
      folderId: string;
      requestBody: {
        folderSource: string;
        driveId?: string;
        siteId?: string;
        sharepointFileId?: string;
        name?: string;
      };
    }) => {
      const response = await workspaceService.updateFolderMetadata(
        workspaceId,
        folderId,
        requestBody
      );
      if (response.success) return response.value;
      else throw restError(response);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [PATH_KEYS.workspace],
        exact: false,
      });
    },
  });

  const moveFolder = useMutation({
    mutationKey: [PATH_KEYS.workspace, "moveFolder"],
    mutationFn: async ({
      workspaceId,
      requestBody,
    }: {
      workspaceId: string;
      requestBody: {
        folderId: string;
        newFolderLocation?: string;
      };
    }) => {
      const response = await workspaceService.moveFolder(
        workspaceId,
        requestBody
      );
      if (response.success) return response.value;
      else throw restError(response);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [PATH_KEYS.workspace],
        exact: false,
      });
    },
  });

  const deleteFolder = useMutation({
    mutationKey: [PATH_KEYS.workspace, "removeFolder"],
    mutationFn: async ({
      workspaceId,
      folderId,
    }: {
      workspaceId: string;
      folderId: string;
    }) => {
      const response = await workspaceService.deleteFolder(
        workspaceId,
        folderId
      );
      if (response.success) return response.value;
      else throw restError(response);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [PATH_KEYS.workspace],
        exact: false,
      });
    },
  });

  const addLink = useMutation({
    mutationKey: [PATH_KEYS.workspace, "addLink"],
    mutationFn: async ({
      workspaceId,
      requestBody,
    }: {
      workspaceId: string;
      requestBody: WorkspaceLinkRequest & { folderId?: string };
    }) => {
      const response = await workspaceService.addLink(workspaceId, requestBody);
      if (response.success) return response.value;
      else throw restError(response);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [PATH_KEYS.workspace],
        exact: false,
      });
    },
  });

  const updateLink = useMutation({
    mutationKey: [PATH_KEYS.workspace, "updateLink"],
    mutationFn: async ({
      workspaceId,
      linkId,
      requestBody,
    }: {
      workspaceId: string;
      linkId: string;
      requestBody: WorkspaceLinkRequest;
    }) => {
      const response = await workspaceService.updateLink(
        workspaceId,
        linkId,
        requestBody
      );
      if (response.success) return response.value;
      else throw restError(response);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [PATH_KEYS.workspace],
        exact: false,
      });
    },
  });

  const moveLink = useMutation({
    mutationKey: [PATH_KEYS.workspace, "moveLink"],
    mutationFn: async ({
      workspaceId,
      linkId,
      requestBody,
    }: {
      workspaceId: string;
      linkId: string;
      requestBody: {
        newFolderId?: string;
      };
    }) => {
      const response = await workspaceService.moveLink(
        workspaceId,
        linkId,
        requestBody
      );
      if (response.success) return response.value;
      else throw restError(response);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [PATH_KEYS.workspace],
        exact: false,
      });
    },
  });

  const deleteLink = useMutation({
    mutationKey: [PATH_KEYS.workspace, "removeLink"],
    mutationFn: async ({
      workspaceId,
      linkId,
    }: {
      workspaceId: string;
      linkId: string;
    }) => {
      const response = await workspaceService.deleteLink(workspaceId, linkId);
      if (response.success) return response.value;
      else throw restError(response);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [PATH_KEYS.workspace],
        exact: false,
      });
    },
  });

  const changeActiveRevision = useMutation({
    mutationKey: [PATH_KEYS.workspace, "changeActiveRevision"],
    mutationFn: async ({
      workspaceId,
      documentId,
      requestBody,
    }: {
      workspaceId: string;
      documentId: string;
      requestBody: {
        newActiveRevisionVersion: number;
      };
    }) => {
      const response = await workspaceService.changeActiveRevision(
        workspaceId,
        documentId,
        requestBody
      );
      if (response.success) return response.value;
      else throw restError(response);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [PATH_KEYS.workspace],
        exact: false,
      });
    },
  });

  /** `pageNumber` is one-based, matching the API. */
  const useQueryOrphanedWorkspaces = (
    pageNumber: number,
    pageSize: number,
    search?: string,
    sortBy: OrphanedWorkspaceSortBy = "Name",
    sortAscending: boolean = true
  ) =>
    useQuery<Pagination<Workspace>>({
      queryKey: [
        PATH_KEYS.workspace,
        "orphaned",
        pageNumber,
        pageSize,
        search,
        sortBy,
        sortAscending,
      ],
      queryFn: async () => {
        const response = await workspaceService.getOrphanedWorkspaces(
          pageNumber,
          pageSize,
          search,
          sortBy,
          sortAscending
        );
        if (response.success) {
          const value = response.value;
          return {
            ...value,
            // API returns totalRecords; normalize to totalCount for consumers
            totalCount: value.totalCount ?? value.totalRecords ?? 0,
          };
        } else throw restError(response);
      },
      // Sorting and paging change the key. Keep the rows already on screen so
      // the table can flag them as stale instead of blanking (LAR-410).
      placeholderData: keepPreviousData,
    });

  const claimWorkspace = useMutation({
    mutationKey: [PATH_KEYS.workspace, "claimWorkspace"],
    mutationFn: ({
      workspaceId,
      newOwnerId,
    }: {
      workspaceId: string;
      newOwnerId: string;
    }) => claimWorkspaceOwnership(workspaceId, newOwnerId),
    onSuccess: invalidateWorkspaceQueries,
  });

  /**** Value ****/
  return {
    ...workspaceCrud,
    remove,
    deleteWorkspace,
    claimWorkspaceOwnership,
    invalidateWorkspaceQueries,
    useGetDocumentHtml,
    useQueryAllWorkspaces,
    addDocuments,
    moveDocuments,
    syncDocument,
    removeDocument,
    reindexDocument,
    removeDocuments,
    grantAccess,
    revokeAccess,
    updateDocumentMetadata,
    addFolder,
    renameDocument,
    updateFolder,
    updateFolderMetadata,
    moveFolder,
    deleteFolder,
    addLink,
    updateLink,
    moveLink,
    deleteLink,
    changeActiveRevision,
    useQueryOrphanedWorkspaces,
    claimWorkspace,
  };
};

export default useWorkspace;
